import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import type { GetDetailsAPIResponse, OrganizationDetails } from './settingstypes';
import { useAppDispatch } from '../../store';
import { useGetDetailsQuery } from './settingsApiSlice';
import { setTenantDetailsResponse } from './tenantSlice';

type UseTenantDetailsOptions = {
  enabled?: boolean;
  refetchOnMountOrArgChange?: boolean;
};

export const useTenantDetails = (options: UseTenantDetailsOptions = {}) => {
  const { enabled = true, refetchOnMountOrArgChange = false } = options;
  const dispatch = useAppDispatch();
  const cachedTenantDetailsResponse = useSelector((state: RootState) => state.tenant.tenantDetailsResponse);
  const authToken = useSelector((state: RootState) => state.auth?.token);
  const tokenKey = typeof authToken === 'string' && authToken.trim() ? authToken.trim() : undefined;
  const hasCachedData = Boolean(cachedTenantDetailsResponse);
  const shouldFetch = enabled && !hasCachedData;

  // Key by token so a call made before login/token sync (no Authorization header) is not reused.
  const queryState = useGetDetailsQuery(tokenKey, {
    skip: !shouldFetch,
    refetchOnMountOrArgChange,
  });

  useEffect(() => {
    if (queryState.data) {
      dispatch(setTenantDetailsResponse(queryState.data as GetDetailsAPIResponse | OrganizationDetails));
    }
  }, [dispatch, queryState.data]);

  const tenantDetailsResponse = (cachedTenantDetailsResponse ?? queryState.data) as GetDetailsAPIResponse | OrganizationDetails | undefined;
  const tenantDetails = ((tenantDetailsResponse as any)?.data ?? tenantDetailsResponse) as OrganizationDetails | undefined;

  return {
    tenantDetailsResponse,
    tenantDetails,
    error: queryState.error,
    isLoading: shouldFetch && queryState.isLoading,
    isFetching: shouldFetch && queryState.isFetching,
    hasCachedData,
  };
};
